import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  BarChart3, Download, Loader2, Play, Save, Trash2, FileSpreadsheet,
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const CHART_COLORS = ['#1e3a5f', '#2563eb', '#0ea5e9', '#14b8a6', '#f59e0b', '#ef4444', '#8b5cf6', '#64748b'];

const todayISO = () => new Date().toISOString().slice(0, 10);
const yearStartISO = () => `${new Date().getFullYear()}-01-01`;

const defaultConfig = () => ({
  group_by: 'consultant',
  metrics: ['hours', 'amount'],
  crosstab: '',
  date_from: yearStartISO(),
  date_to: todayISO(),
  consultant: '',
  client: '',
  project: '',
  approval_status: '',
});

const APPROVAL_OPTIONS = [
  { value: 'pending', label: 'Pendente' },
  { value: 'approved', label: 'Aprovado' },
  { value: 'rejected', label: 'Recusado' },
];

const selectClass =
  'w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500';

const Reports = () => {
  const [metadata, setMetadata] = useState(null);
  const [config, setConfig] = useState(defaultConfig());
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');
  const [chartType, setChartType] = useState('bar');

  // Dados dos filtros
  const [consultants, setConsultants] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);

  // Relatórios salvos
  const [savedReports, setSavedReports] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [metaRes, clientsRes, projectsRes, savedRes] = await Promise.all([
          api.get('/reports/metadata/'),
          api.get('/clients/'),
          api.get('/projects/'),
          api.get('/reports/saved/'),
        ]);
        setMetadata(metaRes.data);
        setClients(clientsRes.data.results || clientsRes.data);
        setProjects(projectsRes.data.results || projectsRes.data);
        setSavedReports(savedRes.data.results || savedRes.data);

        if (metaRes.data.can_filter_consultant) {
          const usersRes = await api.get('/users/?role=consultor');
          setConsultants(usersRes.data.results || usersRes.data);
        }
      } catch {
        toast.error('Erro ao carregar opções de relatório.');
      }
    };
    load();
  }, []);

  const setField = (field, value) => setConfig((prev) => ({ ...prev, [field]: value }));

  const toggleMetric = (metric) => {
    setConfig((prev) => {
      const has = prev.metrics.includes(metric);
      const next = has ? prev.metrics.filter((m) => m !== metric) : [...prev.metrics, metric];
      return { ...prev, metrics: next };
    });
  };

  // Monta o payload removendo filtros vazios
  const buildPayload = () => {
    const payload = { ...config };
    if (!payload.crosstab) delete payload.crosstab;
    for (const f of ['consultant', 'client', 'project', 'approval_status']) {
      if (!payload[f]) delete payload[f];
    }
    return payload;
  };

  const runReport = async (cfg = null) => {
    setLoading(true);
    try {
      const response = await api.post('/reports/run/', { config: cfg || buildPayload() });
      setResult(response.data);
      if (cfg) setConfig({ ...defaultConfig(), ...cfg, crosstab: cfg.crosstab || '' });
    } catch (error) {
      const detail = error.response?.data?.detail || error.response?.data?.config?.[0];
      toast.error(detail || 'Erro ao gerar o relatório.');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    setExporting(format);
    try {
      const response = await api.post(
        '/reports/export/',
        { format, config: buildPayload() },
        { responseType: 'blob' }
      );
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `relatorio.${format}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Relatório ${format.toUpperCase()} exportado.`);
    } catch {
      toast.error('Erro ao exportar o relatório.');
    } finally {
      setExporting('');
    }
  };

  const saveReport = async () => {
    if (!saveName.trim()) {
      toast.error('Informe um nome para salvar o relatório.');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/reports/saved/', {
        name: saveName.trim(),
        config: buildPayload(),
      });
      setSavedReports((prev) => [response.data, ...prev]);
      setSaveName('');
      toast.success('Relatório salvo.');
    } catch (error) {
      const detail = error.response?.data?.name?.[0] || error.response?.data?.config?.[0];
      toast.error(detail || 'Erro ao salvar o relatório.');
    } finally {
      setSaving(false);
    }
  };

  const deleteSaved = async (id) => {
    try {
      await api.delete(`/reports/saved/${id}/`);
      setSavedReports((prev) => prev.filter((r) => r.id !== id));
      toast.success('Relatório excluído.');
    } catch {
      toast.error('Erro ao excluir o relatório salvo.');
    }
  };

  // Colunas numéricas do resultado (exceto o rótulo) para os gráficos
  const numericColumns = useMemo(() => {
    if (!result) return [];
    return result.columns.filter((c) => c.key !== 'label');
  }, [result]);

  const renderChart = () => {
    if (!result || result.rows.length === 0) return null;
    const data = result.rows;

    if (chartType === 'pie') {
      const metric = numericColumns[0];
      if (!metric) return null;
      return (
        <ResponsiveContainer width="100%" height={320}>
          <PieChart>
            <Pie data={data} dataKey={metric.key} nameKey="label" label outerRadius={110}>
              {data.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    const ChartComponent = chartType === 'line' ? LineChart : BarChart;
    const SeriesComponent = chartType === 'line' ? Line : Bar;
    return (
      <ResponsiveContainer width="100%" height={320}>
        <ChartComponent data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip />
          <Legend />
          {numericColumns.map((col, i) => (
            <SeriesComponent
              key={col.key}
              dataKey={col.key}
              name={col.label}
              fill={CHART_COLORS[i % CHART_COLORS.length]}
              stroke={CHART_COLORS[i % CHART_COLORS.length]}
              stackId={result.meta.crosstab ? 'ct' : undefined}
            />
          ))}
        </ChartComponent>
      </ResponsiveContainer>
    );
  };

  if (!metadata) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="animate-spin mr-2" size={20} /> Carregando...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Builder */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-5">
          <BarChart3 size={20} className="text-[#1e3a5f]" />
          <h3 className="text-base font-semibold text-gray-800">Construtor de Relatórios</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Agrupar por</label>
            <select
              className={selectClass}
              value={config.group_by}
              onChange={(e) => setField('group_by', e.target.value)}
            >
              {metadata.group_by.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Pivot (colunas)</label>
            <select
              className={selectClass}
              value={config.crosstab}
              onChange={(e) => setField('crosstab', e.target.value)}
              disabled={config.group_by === 'month'}
            >
              <option value="">Nenhum</option>
              {metadata.crosstab.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">De</label>
            <input
              type="date"
              className={selectClass}
              value={config.date_from}
              onChange={(e) => setField('date_from', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Até</label>
            <input
              type="date"
              className={selectClass}
              value={config.date_to}
              onChange={(e) => setField('date_to', e.target.value)}
            />
          </div>

          {metadata.can_filter_consultant && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Consultor</label>
              <select
                className={selectClass}
                value={config.consultant}
                onChange={(e) => setField('consultant', e.target.value)}
              >
                <option value="">Todos</option>
                {consultants.map((u) => (
                  <option key={u.id} value={u.id}>{u.username}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Cliente</label>
            <select
              className={selectClass}
              value={config.client}
              onChange={(e) => setField('client', e.target.value)}
            >
              <option value="">Todos</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Projeto</label>
            <select
              className={selectClass}
              value={config.project}
              onChange={(e) => setField('project', e.target.value)}
            >
              <option value="">Todos</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status de Aprovação</label>
            <select
              className={selectClass}
              value={config.approval_status}
              onChange={(e) => setField('approval_status', e.target.value)}
            >
              <option value="">Todos</option>
              {APPROVAL_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Métricas */}
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-600 mb-2">Métricas</label>
          <div className="flex flex-wrap gap-2">
            {metadata.metrics.map((m) => {
              const active = config.metrics.includes(m.value);
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => toggleMetric(m.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
          {config.crosstab && (
            <p className="text-xs text-gray-400 mt-1.5">
              No modo pivot, apenas a primeira métrica selecionada é usada nas colunas.
            </p>
          )}
        </div>

        {/* Ações */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <button
            onClick={() => runReport()}
            disabled={loading || config.metrics.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#1e3a5f] hover:bg-[#162d49] text-white text-sm font-semibold rounded-lg shadow-sm transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
            Gerar Relatório
          </button>
          <button
            onClick={() => exportReport('csv')}
            disabled={!!exporting || config.metrics.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 hover:border-blue-400 text-gray-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
          >
            {exporting === 'csv' ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            CSV
          </button>
          <button
            onClick={() => exportReport('xlsx')}
            disabled={!!exporting || config.metrics.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 hover:border-blue-400 text-gray-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
          >
            {exporting === 'xlsx' ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
            Excel
          </button>
        </div>
      </div>

      {/* Resultado */}
      {result && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-base font-semibold text-gray-800">
              Resultado ({result.meta.total_rows} linhas)
            </h3>
            <div className="flex gap-1.5">
              {['bar', 'line', 'pie'].map((t) => (
                <button
                  key={t}
                  onClick={() => setChartType(t)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    chartType === t
                      ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {t === 'bar' ? 'Barras' : t === 'line' ? 'Linhas' : 'Pizza'}
                </button>
              ))}
            </div>
          </div>

          {result.rows.length > 0 ? (
            <>
              {renderChart()}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      {result.columns.map((col) => (
                        <th key={col.key} className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((row, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50/60">
                        {result.columns.map((col) => (
                          <td key={col.key} className="py-2.5 px-3 text-gray-700">
                            {row[col.key] ?? '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-400 py-8 text-center">
              Nenhum dado encontrado para os filtros selecionados.
            </p>
          )}
        </div>
      )}

      {/* Salvar / Salvos */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Save size={18} className="text-[#1e3a5f]" />
          <h3 className="text-base font-semibold text-gray-800">Relatórios Salvos</h3>
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          <input
            type="text"
            placeholder="Nome para salvar a configuração atual"
            className={`${selectClass} flex-1 min-w-[220px]`}
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
          />
          <button
            onClick={saveReport}
            disabled={saving || config.metrics.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-[#1e3a5f] hover:bg-[#162d49] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Salvar
          </button>
        </div>

        {savedReports.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {savedReports.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-2.5">
                <button
                  onClick={() => runReport(r.config)}
                  className="text-sm text-blue-700 hover:text-blue-900 hover:underline font-medium text-left"
                >
                  {r.name}
                </button>
                <button
                  onClick={() => deleteSaved(r.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Excluir"
                >
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-400">Nenhum relatório salvo ainda.</p>
        )}
      </div>
    </div>
  );
};

export default Reports;
